import React, { useState, useEffect, useCallback } from 'react';
import ReversiBoard from './ReversiBoard';
import VictoryEffect from './VictoryEffect';
import GameShareButton from './GameShareButton';
import { GameState, Position, GameMode, Difficulty } from '../types/game';
import {
  createInitialReversiState,
  makeReversiMove,
  undoReversiMove,
  resetReversiGame,
  passTurn
} from '../utils/reversiLogic';
import { getReversiAIDecision, simulateReversiAIThinking } from '../utils/reversiAI';

interface ReversiGameProps {
  gameMode: GameMode;
  difficulty?: Difficulty;
  onBackToMenu?: () => void;
  onBackToModeSelect?: () => void;
}

const ReversiGame: React.FC<ReversiGameProps> = ({ gameMode, difficulty, onBackToMenu, onBackToModeSelect }) => {
  const [gameState, setGameState] = useState<GameState>(() => {
    return createInitialReversiState(gameMode, difficulty);
  });

  const [gameMessage, setGameMessage] = useState<string>('');
  const [showVictoryEffect, setShowVictoryEffect] = useState<boolean>(false);
  const [lastWinner, setLastWinner] = useState<'black' | 'white' | null>(null);
  const [winRecord, setWinRecord] = useState<{ black: number; white: number }>({ black: 0, white: 0 });
  const [isBoardEnlarged, setIsBoardEnlarged] = useState<boolean>(false);
  const [showRules, setShowRules] = useState<boolean>(false);

  // AI回合处理
  const handleAITurn = useCallback(async (currentState: GameState) => {
    if (gameMode !== 'pvc' || currentState.currentPlayer !== 'white' || currentState.status !== 'playing') {
      return;
    }

    // 设置AI思考状态
    setGameState(prev => ({ ...prev, isAIThinking: true }));
    setGameMessage('AI正在思考...');

    // 模拟AI思考时间
    await simulateReversiAIThinking(difficulty || 'medium');

    // 获取AI决策
    const aiMove = getReversiAIDecision(currentState, difficulty || 'medium');
    let newState = currentState;

    if (aiMove) {
      // AI下棋
      newState = makeReversiMove(currentState, aiMove);
      setGameMessage('AI下棋');
    } else {
      // AI无法移动，跳过回合
      newState = passTurn(currentState);
      setGameMessage('AI跳过回合');
    }

    // 清除AI思考状态
    newState = { ...newState, isAIThinking: false };

    setGameState(newState);

    // 清除消息
    setTimeout(() => setGameMessage(''), 2000);
  }, [gameMode, difficulty]);

  // 处理棋盘点击
  const handleCellClick = useCallback((position: Position) => {
    if (gameState.status !== 'playing') return;

    // 人机对战模式下，轮到AI时禁止点击
    if (gameMode === 'pvc' && gameState.currentPlayer === 'white') return;

    // 检查是否为有效移动
    const isValidMove = gameState.validMoves?.some(move =>
      move.row === position.row && move.col === position.col
    );

    if (!isValidMove) {
      setGameMessage('无效移动！');
      setTimeout(() => setGameMessage(''), 1500);
      return;
    }

    // 执行移动
    const newState = makeReversiMove(gameState, position);
    setGameState(newState);
    setGameMessage(`${gameState.currentPlayer === 'black' ? '黑方' : '白方'}下棋`);

    // 清除消息
    setTimeout(() => setGameMessage(''), 2000);
  }, [gameState, gameMode]);

  // 悔棋
  const handleUndo = useCallback(() => {
    if (gameState.moves.length === 0) return;
    const newState = undoReversiMove(gameState);
    setGameState(newState);
    setGameMessage('悔棋成功');
    setTimeout(() => setGameMessage(''), 1500);
  }, [gameState]);

  // 重新开始
  const handleReset = useCallback(() => {
    const newState = resetReversiGame(gameMode, difficulty);
    setGameState(newState);
    setGameMessage('游戏重新开始！');
    setLastWinner(null);
    setShowVictoryEffect(false);
    setTimeout(() => setGameMessage(''), 2000);
  }, [gameMode, difficulty]);

  // 重置胜负记录
  const handleResetScore = useCallback(() => {
    setWinRecord({ black: 0, white: 0 });
    setGameMessage('胜负记录已重置！');
    setTimeout(() => setGameMessage(''), 2000);
  }, []);

  // 切换放大模式
  const toggleBoardEnlarge = useCallback(() => {
    setIsBoardEnlarged(prev => {
      const newMode = !prev;
      setGameMessage(newMode ? '🔍 已切换到老人模式（放大棋盘）' : '📐 已恢复默认布局');
      setTimeout(() => setGameMessage(''), 2000);
      return newMode;
    });
  }, []);

  // 键盘快捷键
  useEffect(() => {
    const handleKeyPress = (event: KeyboardEvent) => {
      if (event.key === 'z' && (event.ctrlKey || event.metaKey)) {
        event.preventDefault();
        handleUndo();
      }
      if (event.key === 'r' && (event.ctrlKey || event.metaKey)) {
        event.preventDefault();
        handleReset();
      }
      if (event.key === 'e' && (event.ctrlKey || event.metaKey)) {
        event.preventDefault();
        toggleBoardEnlarge();
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [handleUndo, handleReset, toggleBoardEnlarge]);

  // AI回合监听
  useEffect(() => {
    if (gameMode === 'pvc' && gameState.currentPlayer === 'white' &&
        gameState.status === 'playing' && !gameState.isAIThinking) {
      handleAITurn(gameState);
    }
  }, [gameState, gameMode, handleAITurn]);

  // 胜利特效监听
  useEffect(() => {
    if (gameState.status === 'finished' && gameState.winner && gameState.winner !== lastWinner) {
      setLastWinner(gameState.winner);
      setWinRecord(prev => ({
        ...prev,
        [gameState.winner!]: prev[gameState.winner!] + 1
      }));
      setShowVictoryEffect(true);

      // 播放胜利音效
      try {
        const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
        const oscillator = audioContext.createOscillator();
        const gainNode = audioContext.createGain();

        oscillator.connect(gainNode);
        gainNode.connect(audioContext.destination);

        // 创建胜利和弦音效（C-E-G和弦）
        oscillator.frequency.setValueAtTime(523.25, audioContext.currentTime); // C5
        oscillator.frequency.setValueAtTime(659.25, audioContext.currentTime + 0.1); // E5
        oscillator.frequency.setValueAtTime(783.99, audioContext.currentTime + 0.2); // G5

        gainNode.gain.setValueAtTime(0.2, audioContext.currentTime);
        gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.8);

        oscillator.start(audioContext.currentTime);
        oscillator.stop(audioContext.currentTime + 0.8);
      } catch (error) {
        console.log('Web Audio API not available, skipping victory sound');
      }
    }
  }, [gameState.status, gameState.winner, lastWinner]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-emerald-50 to-teal-50 flex flex-col">
      {/* 游戏头部 - 统一布局 */}
      <div className="bg-white shadow p-2">
        {/* 标题区域 - 手机端独立行，桌面端居中 */}
        <div className="text-center mb-2 sm:mb-0">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-center sm:space-x-0 sm:space-y-1 space-y-1">
            {/* 美化标题 */}
            <div className="flex items-center justify-center space-x-2">
              <span className="text-2xl">⚫</span>
              <h1 className="text-base sm:text-lg font-bold bg-gradient-to-r from-green-600 via-emerald-600 to-teal-600 bg-clip-text text-transparent sm:mb-1">
                黑白棋
              </h1>
              <span className="text-2xl">⚪</span>
            </div>

            {/* 美化模式描述 */}
            <div className="flex items-center justify-center space-x-2">
              <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-gradient-to-r from-indigo-500 to-purple-500 text-white">
                <span className="w-2 h-2 mr-1 bg-white rounded-full animate-pulse"></span>
                {gameMode === 'pvp' ? '双人对战' : `人机对战`}
              </span>

              {gameMode === 'pvc' && (
                <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                  difficulty === 'easy' ? 'bg-green-100 text-green-800' :
                  difficulty === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                  'bg-red-100 text-red-800'
                }`}>
                  {difficulty === 'easy' ? '😊 简单' : difficulty === 'medium' ? '🎯 中等' : '🔥 困难'}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* 控制按钮区域 */}
        <div className="flex items-center justify-between">
          {/* 左侧按钮组 */}
          <div className="flex items-center space-x-1 sm:space-x-1">
            {/* 首页按钮 */}
            {onBackToMenu && (
              <button
                onClick={onBackToMenu}
                className="flex items-center space-x-1 px-2 py-1.5 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors text-xs font-medium"
                title="回到首页"
              >
                <span>🏠</span>
                <span className="hidden sm:inline">首页</span>
              </button>
            )}

            {/* 返回按钮 */}
            {onBackToModeSelect && (
              <button
                onClick={onBackToModeSelect}
                className="flex items-center space-x-1 px-2 py-1.5 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors text-xs font-medium"
                title="返回模式选择"
              >
                <span>🔙</span>
                <span className="hidden sm:inline">返回</span>
              </button>
            )}

            {/* 重新开始按钮 */}
            <button
              onClick={handleReset}
              className="flex items-center space-x-1 px-2 py-1.5 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors text-xs font-medium"
              title="重新开始"
            >
              <span>🔄</span>
              <span className="hidden sm:inline">重开</span>
            </button>

            {/* 悔棋按钮 */}
            <button
              onClick={handleUndo}
              disabled={gameState.moves.length === 0 || gameState.status !== 'playing' || gameState.isAIThinking}
              className={`flex items-center space-x-1 px-2 py-1.5 rounded-lg transition-colors text-xs font-medium ${
                gameState.moves.length > 0 && gameState.status === 'playing' && !gameState.isAIThinking
                  ? 'bg-blue-500 text-white hover:bg-blue-600'
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              }`}
              title="悔棋"
            >
              <span>↩️</span>
              <span className="hidden sm:inline">悔棋</span>
            </button>
          </div>

          {/* 右侧控制按钮 */}
          <div className="flex items-center space-x-1 sm:space-x-1">
            {/* 游戏规则按钮 */}
            <button
              onClick={() => setShowRules(true)}
              className="flex items-center space-x-1 px-2 py-1.5 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors text-xs font-medium"
              title="游戏规则"
            >
              <span>📖</span>
              <span className="hidden sm:inline">规则</span>
            </button>

            {/* 分享按钮 */}
            <GameShareButton gameType="reversi" />
          </div>
        </div>
      </div>

      {/* 游戏状态信息 - 统一布局 */}
      <div className="bg-white shadow p-2">
        <div className="flex items-center justify-between">
          {/* 当前玩家信息 */}
          <div className="flex items-center space-x-2">
            <div className={`w-4 h-4 rounded-full ${
              gameState.currentPlayer === 'black' ? 'bg-black' : 'bg-white border-2 border-gray-400'
            }`}></div>
            <span className="text-sm sm:text-base font-medium">
              {gameState.status === 'playing' ? (
                <>
                  {gameState.currentPlayer === 'black' ? '黑方' : '白方'}回合
                  {gameState.isAIThinking && gameMode === 'pvc' && gameState.currentPlayer === 'white' && (
                    <span className="text-xs sm:text-sm text-blue-600 animate-pulse ml-2">AI思考中...</span>
                  )}
                </>
              ) : (
                <span className="text-green-600">
                  {gameState.winner ? (
                    <span>{gameState.winner === 'black' ? '黑方' : '白方'}获胜！</span>
                  ) : '游戏结束'}
                </span>
              )}
            </span>
            {/* 游戏消息 */}
            {gameMessage && (
              <span className="text-center text-yellow-800 text-sm font-medium">
                {gameMessage}
              </span>
            )}
          </div>

          {/* 棋子数量和步数信息 */}
          <div className="flex items-center space-x-3 text-sm sm:text-base text-gray-600">
            <span>⚫ {gameState.blackScore}</span>
            <span>⚪ {gameState.whiteScore}</span>
            <span>步数: {gameState.moves.length + 1}</span>
          </div>
        </div>

        {/* 简化的战绩信息 */}
        {(winRecord.black > 0 || winRecord.white > 0) && (
          <div className="mt-2 pt-2 border-t border-gray-200">
            <div className="flex justify-center space-x-4 text-xs text-gray-600">
              <span>黑方胜场: {winRecord.black}</span>
              <span>白方胜场: {winRecord.white}</span>
              {winRecord.black + winRecord.white > 0 && (
                <span>胜率: {((winRecord.black / (winRecord.black + winRecord.white)) * 100).toFixed(0)}%</span>
              )}
            </div>
          </div>
        )}
      </div>

      {/* 棋盘 - 移动端满屏布局 */}
      <div className="flex-1 flex items-center justify-center px-1 py-1 xs:px-1 xs:py-2">
        <div className="w-full h-full max-w-none">
          <ReversiBoard
            board={gameState.board}
            onCellClick={handleCellClick}
            disabled={gameState.status !== 'playing' || gameState.isAIThinking || (gameMode === 'pvc' && gameState.currentPlayer === 'white')}
            validMoves={gameState.validMoves}
            lastMove={gameState.moves.length > 0 ? gameState.moves[gameState.moves.length - 1].position : undefined}
            lastFlippedPieces={gameState.lastFlippedPieces}
            blackScore={gameState.blackScore}
            whiteScore={gameState.whiteScore}
            currentPlayer={gameState.currentPlayer}
            enlarged={isBoardEnlarged}
          />
        </div>
      </div>



      {/* 游戏规则对话框 */}
      {showRules && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl p-6 max-w-4xl w-full max-h-[80vh] overflow-y-auto relative">
            {/* 右上角关闭按钮 */}
            <button
              onClick={() => setShowRules(false)}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors text-2xl font-bold w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100"
              title="关闭"
            >
              ×
            </button>

            <h2 className="text-xl font-bold mb-4 text-gray-800 flex items-center pr-8">
              <span className="text-2xl mr-2">📖</span>
              黑白棋游戏规则
            </h2>

            <div className="text-gray-700 space-y-3 text-sm">
              <div>
                <h3 className="font-semibold text-gray-800 mb-1">🎯 游戏目标</h3>
                <p>在游戏结束时，棋盘上棋子数量较多的一方获胜。</p>
              </div>

              <div>
                <h3 className="font-semibold text-gray-800 mb-1">🎮 游戏规则</h3>
                <ul className="list-disc list-inside space-y-1 ml-2">
                  <li>游戏在8×8的棋盘上进行，初始位置中心有2黑2白交叉放置</li>
                  <li>黑方先行，双方轮流下棋</li>
                  <li>每次下棋必须夹住对手的棋子（横、竖、斜方向均可）</li>
                  <li>被夹住的对手棋子会翻转成己方颜色</li>
                  <li>无法下棋的一方必须跳过回合</li>
                  <li>双方都无法下棋时游戏结束</li>
                </ul>
              </div>

              <div>
                <h3 className="font-semibold text-gray-800 mb-1">💡 策略提示</h3>
                <ul className="list-disc list-inside space-y-1 ml-2">
                  <li>角落位置价值最高，无法被对手翻转</li>
                  <li>边缘位置次之，也相对安全</li>
                  <li>避免过早占领过多棋子（前期控制更重要）</li>
                </ul>
              </div>

              <div>
                <h3 className="font-semibold text-gray-800 mb-1">🔧 操作提示</h3>
                <ul className="list-disc list-inside space-y-1 ml-2">
                  <li>点击绿色高亮位置下棋</li>
                  <li>使用"悔棋"按钮撤销上一步</li>
                  <li>使用"重开"按钮重新开始游戏</li>
                </ul>
              </div>
            </div>

            <div className="mt-6 flex justify-end space-x-3">
              <button
                onClick={() => setShowRules(false)}
                className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors text-sm font-medium"
              >
                关闭
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 游戏规则提示 - 统一布局 */}
      <div className="bg-green-50 border-t border-green-200 p-2 sm:p-3">
        <p className="text-sm sm:text-base text-green-800 text-center leading-relaxed">
          <strong>黑白棋规则：</strong>8×8棋盘{isBoardEnlarged && <span className="ml-3 text-orange-600">🔍 老人模式</span>}，落子需夹住对手棋子并翻转 |
          角落位置价值最高 | 无法落子时跳过回合 | 棋子多的一方获胜
        </p>
      </div>

      {/* 胜利特效 */}
      {showVictoryEffect && lastWinner && (
        <VictoryEffect
          winner={lastWinner}
          isVisible={showVictoryEffect}
          onHide={() => {
            setShowVictoryEffect(false);
            // 隐藏特效后自动重新开始游戏
            handleReset();
          }}
        />
      )}
    </div>
  );
};

export default ReversiGame;